/**
 * Live Trading V3 — the Attack target derivation, in ONE place.
 *
 * "Attack" means: take the level above the passive reference, or establish
 * it when empty. In every book's own-side coordinate space (moneyline NO
 * books and spread/total YES/NO columns alike), "the level above" is exactly
 * one price step HIGHER than the best same-side displayed bid:
 *
 *   reference = best same-side bid (the maker price the surface displays)
 *   target    = reference + depth   (depth is fixed at 1 in this release)
 *
 * A limit BUY at the target with postOnly false then does both halves of the
 * contract in one order: if opposing liquidity is displayed at the target it
 * fills (intentional taker), and any remainder rests AT the target under the
 * ordinary one-click GTC semantics — establishing the level. It can never
 * pay more than the target, and it can never exceed the configured attack
 * size, which is what makes the aggression bounded.
 *
 * Fail-closed rules, matching the A/D displayed-price discipline:
 *   • no same-side displayed bid → no reference → null (the trader is not
 *     looking at a passive level to attack above);
 *   • fee policy hiding the surface's prices hides the attack too — the
 *     keyboard must never act on a price the pointer can't see;
 *   • a target above 99¢ is out of the venue's range → null.
 */
import { pillPriceCents } from '@/views/ValueDashboard/ladder/helpers';
import type { VenueBook } from '@/views/ValueDashboard/types';
import { ATTACK_DEPTH_CONTRACT } from '@/lib/trading/attackPolicyClient';

export interface Lt2AttackTarget {
  /** The best same-side displayed bid the target was computed from. */
  referencePriceCents: number;
  /** reference + depth — the bounded limit price W/E submit. */
  targetPriceCents: number;
  /** The depth used (fixed at 1 in this release; carried for audit). */
  depth: number;
}

export function deriveAttackTarget(
  book: VenueBook | null,
  side: 'yes' | 'no',
  priceHidden: boolean,
): Lt2AttackTarget | null {
  if (priceHidden || book == null) return null;
  // The same maker read the surfaces display for the passive side: best
  // same-side bid, with the helpers' own realness filtering.
  return attackTargetAtPrice(pillPriceCents(book, side, 'maker'));
}

/**
 * The bounded target for ONE displayed passive price, which is the whole policy
 * above with the reference handed in rather than read off the top of book.
 *
 * Sniper v2 Phase 2 needs it: the level cursor lets W/E act at a rung BELOW the
 * best bid, and "the level above the passive reference" then means the level
 * above THAT rung. Keeping it here rather than at the call site preserves this
 * module's invariant — the depth, the integer discipline and the 1..99 venue
 * bound live in one place, so a surface can never publish a rung target that
 * disagrees with the top-of-book one.
 *
 * `deriveAttackTarget(book, side, false)` is exactly
 * `attackTargetAtPrice(pillPriceCents(book, side, 'maker'))`, which is what lets
 * a card publish its level-0 attack verbatim and derive the rest.
 */
export function attackTargetAtPrice(
  referencePriceCents: number | null | undefined,
): Lt2AttackTarget | null {
  if (referencePriceCents == null || !Number.isInteger(referencePriceCents)) return null;
  const target = referencePriceCents + ATTACK_DEPTH_CONTRACT;
  if (target < 1 || target > 99) return null;
  return {
    referencePriceCents,
    targetPriceCents: target,
    depth: ATTACK_DEPTH_CONTRACT,
  };
}
